import React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { downloadPng } from 'helpers/helpers';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getTitle } from 'routes/Explore/hooks/exportToFile';
import { useSettingsContext } from 'routes/Explore/components/Contexts/SettingsContext/SettingsContext';

type Props = {
    open: boolean;
    title: string;
    onClose: () => void;
    children: React.ReactNode;
};

const BACKGROUND_COLOR_OPTIONS = {
    TRANSPARENT: 'transparent',
    WHITE: 'white',
} as const;

type BackgroundColor =
    (typeof BACKGROUND_COLOR_OPTIONS)[keyof typeof BACKGROUND_COLOR_OPTIONS];

const getDefaultBackgroundColor = (): BackgroundColor => {
    const storedBackgroundColor = localStorage.getItem(
        'pngExportBackgroundColor',
    );
    return storedBackgroundColor === BACKGROUND_COLOR_OPTIONS.WHITE
        ? BACKGROUND_COLOR_OPTIONS.WHITE
        : BACKGROUND_COLOR_OPTIONS.TRANSPARENT;
};

const ChartPngPreview = ({
    open,
    title,
    onClose,
    children,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { chartTitle } = useSettingsContext();

    const [backgroundColor, setBackgroundColor] =
        React.useState<BackgroundColor>(getDefaultBackgroundColor());

    const onBackgroundColorChange = (newColor: BackgroundColor) => {
        setBackgroundColor(newColor);
        localStorage.setItem('pngExportBackgroundColor', newColor);
    };

    const onExport = () => {
        // TODO: Support export of table as images.
        // Right now when the table overflows it only shows the content that is on-screen.
        downloadPng(
            'chart-draw-container',
            getTitle(title, chartTitle),
            backgroundColor,
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false}>
            <DialogTitle>{t('sendDashboard.waitForPreview')}</DialogTitle>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={backgroundColor}
                    exclusive
                    onChange={(event, newBackground) => {
                        onBackgroundColorChange(newBackground);
                    }}
                >
                    {Object.values(BACKGROUND_COLOR_OPTIONS).map((color) => (
                        <ToggleButton key={color} value={color}>
                            {t('backgroundColor', {
                                color: t(`color.${color}`),
                            })}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </DialogActions>
            <DialogContent
                style={{
                    width: 'calc(100vw - 200px)',
                    maxWidth: 1500,
                    height: 750,
                }}
                id="chart-draw-container"
            >
                <Typography variant="h4">{title}</Typography>
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
                <Button
                    onClick={onExport}
                    variant="contained"
                    data-testid="download-png"
                >
                    {t('common.download')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChartPngPreview;
